import React, { useEffect, useState } from "react";
import { Nav } from "react-bootstrap";
import {
  FaPaperPlane,
  FaArrowRight,
  FaTelegramPlane,
  FaRocket,
  FaCheck,
} from "react-icons/fa";
import { IconType } from "react-icons";
import { createElement } from "react";
import { IconBaseProps } from "react-icons";
import LogoUploadField from "../../../../components/common/LogoUploadField";
import { resolveLogoUrl } from "../../../../utils/urlUtils";

type WidgetSettings = {
  launcher: {
    iconUrl: string;
    iconFit?: "cover" | "contain";
    sizeVariant: "small" | "normal" | "large";
    primaryColor: string;
    shape: "circle" | "rounded" | "square";
    shadow: boolean;
    position: "bottom-right" | "bottom-left" | "top-right" | "top-left";
    animation: "none" | "bounce" | "pulse" | "spin" | "spin-reverse";
  };
  window: {
    header: {
      title: string;
      background: string;
      titleColor: string;
    };
    input: {
      placeholder: string;
      bg: string;
      text: string;
      border: string;
    };
    sendButton: {
      bg: string;
      text: string;
      buttonText: string;
      type: "text" | "icon";
      icon: string;
    };
    message: {
      fontSize: number;
      userBubble: { bg: string; text: string };
      botBubble: { bg: string; text: string };
    };
  };
  advanced: {
    window: { width: number; height: number };
  };
};

const DEFAULT_SETTINGS: WidgetSettings = {
  launcher: {
    iconUrl: "",
    iconFit: "cover",
    sizeVariant: "normal",
    primaryColor: "#667eea",
    shape: "circle",
    shadow: true,
    position: "bottom-right",
    animation: "none",
  },
  window: {
    header: {
      title: "How can we help?",
      background: "#667eea",
      titleColor: "#ffffff",
    },
    input: {
      placeholder: "Type your message...",
      bg: "#ffffff",
      text: "#111827",
      border: "#e5e7eb",
    },
    sendButton: {
      bg: "#667eea",
      text: "#ffffff",
      buttonText: "Send",
      type: "text",
      icon: "send",
    },
    message: {
      fontSize: 14,
      userBubble: { bg: "#eef2ff", text: "#111827" },
      botBubble: { bg: "#f1f5f9", text: "#111827" },
    },
  },
  advanced: {
    window: { width: 360, height: 520 },
  },
};

interface WidgetCustomizationBuilderProps {
  value?: Partial<WidgetSettings> | null;
  onChange?: (next: WidgetSettings) => void;
  companyId?: number | string;
}

// Available icons for send button
const SEND_BUTTON_ICONS = [
  { value: "send", label: "Send", icon: FaPaperPlane },
  { value: "arrow", label: "Arrow", icon: FaArrowRight },
  { value: "telegram", label: "Telegram", icon: FaTelegramPlane },
  { value: "rocket", label: "Rocket", icon: FaRocket },
  { value: "check", label: "Check", icon: FaCheck },
] as const;

// Helper function to render icon
const renderIcon = (iconValue: string, size: number = 16) => {
  const props: IconBaseProps = { size };

  switch (iconValue) {
    case "send":
      return createElement(
        FaPaperPlane as React.ComponentType<IconBaseProps>,
        props
      );
    case "arrow":
      return createElement(
        FaArrowRight as React.ComponentType<IconBaseProps>,
        props
      );
    case "telegram":
      return createElement(
        FaTelegramPlane as React.ComponentType<IconBaseProps>,
        props
      );
    case "rocket":
      return createElement(
        FaRocket as React.ComponentType<IconBaseProps>,
        props
      );
    case "check":
      return createElement(
        FaCheck as React.ComponentType<IconBaseProps>,
        props
      );
    default:
      return createElement(
        FaPaperPlane as React.ComponentType<IconBaseProps>,
        props
      );
  }
};

function mergeSettings(
  base: WidgetSettings,
  incoming: Partial<WidgetSettings> | null | undefined
): WidgetSettings {
  const b = base;
  const v = incoming || {};
  return {
    launcher: {
      iconUrl: v.launcher?.iconUrl ?? b.launcher.iconUrl,
      iconFit: v.launcher?.iconFit ?? b.launcher.iconFit ?? "cover",
      sizeVariant: v.launcher?.sizeVariant ?? b.launcher.sizeVariant,
      primaryColor: v.launcher?.primaryColor ?? b.launcher.primaryColor,
      shape: v.launcher?.shape ?? b.launcher.shape,
      shadow: v.launcher?.shadow ?? b.launcher.shadow,
      position: v.launcher?.position ?? b.launcher.position,
      animation: v.launcher?.animation ?? b.launcher.animation,
    },
    window: {
      header: {
        title: v.window?.header?.title ?? b.window.header.title,
        background: v.window?.header?.background ?? b.window.header.background,
        titleColor: v.window?.header?.titleColor ?? b.window.header.titleColor,
      },
      input: {
        placeholder:
          v.window?.input?.placeholder ?? b.window.input.placeholder,
        bg: v.window?.input?.bg ?? b.window.input.bg,
        text: v.window?.input?.text ?? b.window.input.text,
        border: v.window?.input?.border ?? b.window.input.border,
      },
      sendButton: {
        bg: v.window?.sendButton?.bg ?? b.window.sendButton.bg,
        text: v.window?.sendButton?.text ?? b.window.sendButton.text,
        buttonText:
          v.window?.sendButton?.buttonText ?? b.window.sendButton.buttonText,
        type: v.window?.sendButton?.type ?? b.window.sendButton.type,
        icon: v.window?.sendButton?.icon ?? b.window.sendButton.icon,
      },
      message: {
        fontSize: v.window?.message?.fontSize ?? b.window.message.fontSize,
        userBubble: {
          bg:
            v.window?.message?.userBubble?.bg ??
            b.window.message.userBubble.bg,
          text:
            v.window?.message?.userBubble?.text ??
            b.window.message.userBubble.text,
        },
        botBubble: {
          bg:
            v.window?.message?.botBubble?.bg ??
            b.window.message.botBubble.bg,
          text:
            v.window?.message?.botBubble?.text ??
            b.window.message.botBubble.text,
        },
      },
    },
    advanced: {
      window: {
        width: v.advanced?.window?.width ?? b.advanced.window.width,
        height: v.advanced?.window?.height ?? b.advanced.window.height,
      },
    },
  };
}

const WidgetCustomizationBuilder: React.FC<WidgetCustomizationBuilderProps> = ({
  value,
  onChange,
  companyId,
}) => {
  const [settings, setSettings] = useState<WidgetSettings>(() =>
    mergeSettings(DEFAULT_SETTINGS, value)
  );
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("launcher");
  const numericCompanyId =
    typeof companyId === "string"
      ? parseInt(companyId, 10) || undefined
      : companyId;

  useEffect(() => {
    const merged = mergeSettings(DEFAULT_SETTINGS, value);
    // Avoid clobbering local edits if props round-trip equal
    try {
      if (JSON.stringify(merged) !== JSON.stringify(settings)) {
        setSettings(merged);
      }
    } catch {
      setSettings(merged);
    }
  }, [value, settings]);

  const updateSetting = (path: string, value: any) => {
    setSettings((prev) => {
      const newSettings = { ...prev } as any;
      const keys = path.split(".");
      let current: any = newSettings;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        current[k] = { ...(current[k] || {}) };
        current = current[k];
      }
      current[keys[keys.length - 1]] = value;
      if (onChange) {
        try {
          onChange(newSettings as WidgetSettings);
        } catch { }
      }
      return newSettings as WidgetSettings;
    });
  };

  const getLauncherSize = () => {
    switch (settings.launcher.sizeVariant) {
      case "small":
        return 48;
      case "large":
        return 72;
      default:
        return 60;
    }
  };

  const getLauncherRadius = () => {
    switch (settings.launcher.shape) {
      case "square":
        return "8px";
      case "rounded":
        return "16px";
      default:
        return "50%";
    }
  };

  const getPosition = () => {
    const pos = settings.launcher.position;
    const style: React.CSSProperties = { position: "absolute" };

    if (pos.includes("bottom")) style.bottom = "16px";
    if (pos.includes("top")) style.top = "16px";
    if (pos.includes("right")) style.right = "16px";
    if (pos.includes("left")) style.left = "16px";

    return style;
  };

  const tabs = [
    { id: "launcher", label: "Launcher", className: "widget-tab-launcher" },
    { id: "header", label: "Header", className: "widget-tab-header" },
    { id: "input", label: "Input", className: "widget-tab-input" },
    { id: "messages", label: "Messages", className: "widget-tab-messages" },
    { id: "advanced", label: "Advanced", className: "widget-tab-advanced" },
  ];

  return (
    <div
      className="widget-customization"
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      <div className="widget-builder-grid">
        {/* Left Column: Settings Panel */}
        <div className="widget-builder-settings">
          {/* Tabs */}
          <Nav variant="pills" className="chatbots-double-tabs">
            {tabs.map((tab) => (
              <Nav.Item key={tab.id} className={tab.className}>
                <Nav.Link
                  eventKey={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="chatbots-double-tabs-link"
                >
                  {tab.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          {/* Form Content */}
          <div style={{ paddingBottom: "20px" }}>
            {activeTab === "launcher" && (
              <div className="d-flex flex-column gap-3">
                <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                  <span style={{ width: "4px", height: "18px", background: "var(--brand-primary)", borderRadius: "2px" }}></span>
                  Launcher Configuration
                </h6>
                {/* Position, Size, Shape - Yan yana */}
                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <label className="form-label-custom">Position</label>
                    <select
                      id="widget_position_select"
                      name="widget_position"
                      value={settings.launcher.position}
                      onChange={(e) =>
                        updateSetting("launcher.position", e.target.value)
                      }
                      className="form-control-custom"
                    >
                      <option value="bottom-right">Bottom Right</option>
                      <option value="bottom-left">Bottom Left</option>
                      <option value="top-right">Top Right</option>
                      <option value="top-left">Top Left</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label-custom">Size</label>
                    <select
                      id="widget_size_select"
                      name="widget_size"
                      value={settings.launcher.sizeVariant}
                      onChange={(e) =>
                        updateSetting("launcher.sizeVariant", e.target.value)
                      }
                      className="form-control-custom"
                    >
                      <option value="small">Small</option>
                      <option value="normal">Normal</option>
                      <option value="large">Large</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label-custom">Shape</label>
                    <select
                      id="widget_shape_select"
                      name="widget_shape"
                      value={settings.launcher.shape}
                      onChange={(e) =>
                        updateSetting("launcher.shape", e.target.value)
                      }
                      className="form-control-custom"
                    >
                      <option value="circle">Circle</option>
                      <option value="rounded">Rounded</option>
                      <option value="square">Square</option>
                    </select>
                  </div>
                </div>

                {/* Logo URL - Tam genişlik */}
                <div>
                  <label className="form-label-custom">Logo URL</label>
                  <input
                    type="url"
                    id="widget_logo_url_input"
                    placeholder="https://..."
                    value={settings.launcher.iconUrl}
                    onChange={(e) =>
                      updateSetting("launcher.iconUrl", e.target.value)
                    }
                    className="form-control-custom mb-2"
                  />

                  {numericCompanyId && (
                    <LogoUploadField
                      companyId={numericCompanyId}
                      value={settings.launcher.iconUrl || ""}
                      onChange={(url) => {
                        // Handle empty string (remove) or non-base64 URLs
                        if (!url || url === "") {
                          updateSetting("launcher.iconUrl", "");
                        } else if (!url.startsWith("data:")) {
                          // For safety, avoid storing base64 data URLs in widget settings
                          updateSetting("launcher.iconUrl", resolveLogoUrl(url));
                        }
                      }}
                      onLogoUrlChange={(logoUrl) => {
                        // Handle empty string (remove) or valid logo URLs
                        if (!logoUrl || logoUrl === "") {
                          updateSetting("launcher.iconUrl", "");
                        } else {
                          // Always store full URL so widget works on external sites
                          const full = resolveLogoUrl(logoUrl);
                          updateSetting("launcher.iconUrl", full);
                        }
                      }}
                      label=""
                      hideUrlInput={true}
                      helperText="Upload an image. The stored value will always be a full URL."
                    />
                  )}
                </div>

                {/* Logo Fit ve Primary Color - Yan yana */}
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label-custom">Logo Fit</label>
                    <select
                      value={settings.launcher.iconFit}
                      onChange={(e) =>
                        updateSetting("launcher.iconFit", e.target.value)
                      }
                      className="form-control-custom"
                    >
                      <option value="cover">Cover</option>
                      <option value="contain">Contain</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label-custom">Primary Color</label>
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          flexShrink: 0,
                          border: "1px solid #e2e8f0"
                        }}
                      >
                        <input
                          type="color"
                          value={settings.launcher.primaryColor}
                          onChange={(e) =>
                            updateSetting("launcher.primaryColor", e.target.value)
                          }
                          style={{
                            width: "150%",
                            height: "150%",
                            padding: 0,
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            transform: "translate(-25%, -25%)"
                          }}
                        />
                      </div>
                      <input
                        type="text"
                        value={settings.launcher.primaryColor}
                        onChange={(e) => updateSetting("launcher.primaryColor", e.target.value)}
                        className="form-control-custom"
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                </div>

                {/* Animation ve Enable Shadow - Yan yana */}
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label-custom">Animation</label>
                    <select
                      value={settings.launcher.animation}
                      onChange={(e) =>
                        updateSetting("launcher.animation", e.target.value)
                      }
                      className="form-control-custom"
                    >
                      <option value="none">None</option>
                      <option value="bounce">Bounce</option>
                      <option value="pulse">Pulse</option>
                      <option value="spin">Spin</option>
                      <option value="spin-reverse">Spin Reverse</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label-custom" style={{ marginBottom: "8px", display: "block" }}>Shadow</label>
                    <div className="form-check form-switch ps-0 d-flex align-items-center" style={{ height: "38px" }}>
                      <input
                        className="form-check-input ms-0"
                        type="checkbox"
                        id="launcherShadow"
                        checked={settings.launcher.shadow}
                        onChange={(e) =>
                          updateSetting("launcher.shadow", e.target.checked)
                        }
                      />
                      <label className="form-check-label" htmlFor="launcherShadow" style={{ fontSize: '14px', fontWeight: 500, color: '#0f172a', marginLeft: "8px" }}>
                        Enable Shadow
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "header" && (
              <div className="d-flex flex-column gap-3">
                <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                  <span style={{ width: "4px", height: "18px", background: "var(--brand-primary)", borderRadius: "2px" }}></span>
                  Window Header
                </h6>
                <div>
                  <label className="form-label-custom">Title</label>
                  <input
                    type="text"
                    id="widget_header_title_input"
                    name="widget_header_title"
                    value={settings.window.header.title}
                    onChange={(e) =>
                      updateSetting("window.header.title", e.target.value)
                    }
                    className="form-control-custom"
                  />
                </div>

                <div>
                  <label className="form-label-custom">Background Color</label>
                  <div className="d-flex align-items-center gap-2">
                    <div
                      style={{
                        width: "38px",
                        height: "38px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        flexShrink: 0,
                        border: "1px solid #e2e8f0"
                      }}
                    >
                      <input
                        type="color"
                        value={settings.window.header.background}
                        onChange={(e) =>
                          updateSetting("window.header.background", e.target.value)
                        }
                        style={{
                          width: "150%",
                          height: "150%",
                          padding: 0,
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          transform: "translate(-25%, -25%)"
                        }}
                      />
                    </div>
                    <input
                      type="text"
                      value={settings.window.header.background}
                      onChange={(e) => updateSetting("window.header.background", e.target.value)}
                      className="form-control-custom"
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label-custom">Text Color</label>
                  <div className="d-flex align-items-center gap-2">
                    <div
                      style={{
                        width: "38px",
                        height: "38px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        flexShrink: 0,
                        border: "1px solid #e2e8f0"
                      }}
                    >
                      <input
                        type="color"
                        value={settings.window.header.titleColor}
                        onChange={(e) =>
                          updateSetting("window.header.titleColor", e.target.value)
                        }
                        style={{
                          width: "150%",
                          height: "150%",
                          padding: 0,
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          transform: "translate(-25%, -25%)"
                        }}
                      />
                    </div>
                    <input
                      type="text"
                      value={settings.window.header.titleColor}
                      onChange={(e) => updateSetting("window.header.titleColor", e.target.value)}
                      className="form-control-custom"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "input" && (
              <div className="d-flex flex-column gap-3">
                <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                  <span style={{ width: "4px", height: "18px", background: "var(--brand-primary)", borderRadius: "2px" }}></span>
                  Input Area
                </h6>
                {/* Placeholder - Tek başına */}
                <div>
                  <label className="form-label-custom">Placeholder</label>
                  <input
                    type="text"
                    id="widget_input_placeholder_input"
                    name="widget_input_placeholder"
                    value={settings.window.input.placeholder}
                    onChange={(e) =>
                      updateSetting("window.input.placeholder", e.target.value)
                    }
                    className="form-control-custom"
                  />
                </div>

                {/* Input Background, Input Text Color, Input Border Color - Yan yana (3 sütun) */}
                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <label className="form-label-custom">Input Background</label>
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          flexShrink: 0,
                          border: "1px solid #e2e8f0"
                        }}
                      >
                        <input
                          type="color"
                          value={settings.window.input.bg}
                          onChange={(e) =>
                            updateSetting("window.input.bg", e.target.value)
                          }
                          style={{
                            width: "150%",
                            height: "150%",
                            padding: 0,
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            transform: "translate(-25%, -25%)"
                          }}
                        />
                      </div>
                      <input
                        type="text"
                        value={settings.window.input.bg}
                        onChange={(e) => updateSetting("window.input.bg", e.target.value)}
                        className="form-control-custom"
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label-custom">Input Text Color</label>
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          flexShrink: 0,
                          border: "1px solid #e2e8f0"
                        }}
                      >
                        <input
                          type="color"
                          value={settings.window.input.text}
                          onChange={(e) =>
                            updateSetting("window.input.text", e.target.value)
                          }
                          style={{
                            width: "150%",
                            height: "150%",
                            padding: 0,
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            transform: "translate(-25%, -25%)"
                          }}
                        />
                      </div>
                      <input
                        type="text"
                        value={settings.window.input.text}
                        onChange={(e) => updateSetting("window.input.text", e.target.value)}
                        className="form-control-custom"
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label-custom">Input Border Color</label>
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          flexShrink: 0,
                          border: "1px solid #e2e8f0"
                        }}
                      >
                        <input
                          type="color"
                          value={settings.window.input.border}
                          onChange={(e) =>
                            updateSetting("window.input.border", e.target.value)
                          }
                          style={{
                            width: "150%",
                            height: "150%",
                            padding: 0,
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            transform: "translate(-25%, -25%)"
                          }}
                        />
                      </div>
                      <input
                        type="text"
                        value={settings.window.input.border}
                        onChange={(e) => updateSetting("window.input.border", e.target.value)}
                        className="form-control-custom"
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                </div>

                {/* Button Type ve Button Text - Yan yana (2 sütun) */}
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label-custom">Button Type</label>
                    <select
                      value={settings.window.sendButton.type}
                      onChange={(e) =>
                        updateSetting(
                          "window.sendButton.type",
                          e.target.value as "text" | "icon"
                        )
                      }
                      className="form-control-custom"
                    >
                      <option value="text">Text</option>
                      <option value="icon">Icon</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-6">
                    {settings.window.sendButton.type === "text" ? (
                      <>
                        <label className="form-label-custom">Button Text</label>
                        <input
                          type="text"
                          value={settings.window.sendButton.buttonText}
                          onChange={(e) =>
                            updateSetting(
                              "window.sendButton.buttonText",
                              e.target.value
                            )
                          }
                          placeholder="e.g., Send"
                          className="form-control-custom"
                        />
                      </>
                    ) : (
                      <>
                        <label className="form-label-custom">Select Icon</label>
                        <div className="d-flex gap-2 flex-wrap">
                          {SEND_BUTTON_ICONS.map((iconOption) => (
                            <button
                              key={iconOption.value}
                              type="button"
                              onClick={() =>
                                updateSetting(
                                  "window.sendButton.icon",
                                  iconOption.value
                                )
                              }
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "40px",
                                height: "40px",
                                border:
                                  settings.window.sendButton.icon === iconOption.value
                                    ? "2px solid #3b82f6"
                                    : "1px solid #e2e8f0",
                                borderRadius: "8px",
                                background:
                                  settings.window.sendButton.icon === iconOption.value
                                    ? "#eff6ff"
                                    : "#ffffff",
                                color:
                                  settings.window.sendButton.icon === iconOption.value
                                    ? "#3b82f6"
                                    : "#64748b",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                              }}
                              title={iconOption.label}
                            >
                              {renderIcon(iconOption.value, 16)}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Send Button Color ve Button Text Color - Yan yana (2 sütun) */}
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label-custom">Send Button Color</label>
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          flexShrink: 0,
                          border: "1px solid #e2e8f0"
                        }}
                      >
                        <input
                          type="color"
                          value={settings.window.sendButton.bg}
                          onChange={(e) =>
                            updateSetting("window.sendButton.bg", e.target.value)
                          }
                          style={{
                            width: "150%",
                            height: "150%",
                            padding: 0,
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            transform: "translate(-25%, -25%)"
                          }}
                        />
                      </div>
                      <input
                        type="text"
                        value={settings.window.sendButton.bg}
                        onChange={(e) => updateSetting("window.sendButton.bg", e.target.value)}
                        className="form-control-custom"
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label-custom">Button Text Color</label>
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          flexShrink: 0,
                          border: "1px solid #e2e8f0"
                        }}
                      >
                        <input
                          type="color"
                          value={settings.window.sendButton.text}
                          onChange={(e) =>
                            updateSetting("window.sendButton.text", e.target.value)
                          }
                          style={{
                            width: "150%",
                            height: "150%",
                            padding: 0,
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            transform: "translate(-25%, -25%)"
                          }}
                        />
                      </div>
                      <input
                        type="text"
                        value={settings.window.sendButton.text}
                        onChange={(e) => updateSetting("window.sendButton.text", e.target.value)}
                        className="form-control-custom"
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "messages" && (
              <div className="d-flex flex-column gap-3">
                <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                  <span style={{ width: "4px", height: "18px", background: "var(--brand-primary)", borderRadius: "2px" }}></span>
                  Message Bubbles
                </h6>
                <div>
                  <label className="form-label-custom">Font Size: {settings.window.message.fontSize}px</label>
                  <input
                    type="range"
                    min="12"
                    max="18"
                    value={settings.window.message.fontSize}
                    onChange={(e) =>
                      updateSetting(
                        "window.message.fontSize",
                        Number(e.target.value)
                      )
                    }
                    style={{ width: "100%", cursor: "pointer" }}
                    className="form-range"
                  />
                </div>

                <div>
                  <label className="form-label-custom">User Bubble Color</label>
                  <div className="d-flex align-items-center gap-2">
                    <div
                      style={{
                        width: "38px",
                        height: "38px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        flexShrink: 0,
                        border: "1px solid #e2e8f0"
                      }}
                    >
                      <input
                        type="color"
                        value={settings.window.message.userBubble.bg}
                        onChange={(e) =>
                          updateSetting(
                            "window.message.userBubble.bg",
                            e.target.value
                          )
                        }
                        style={{
                          width: "150%",
                          height: "150%",
                          padding: 0,
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          transform: "translate(-25%, -25%)"
                        }}
                      />
                    </div>
                    <input
                      type="text"
                      value={settings.window.message.userBubble.bg}
                      onChange={(e) => updateSetting("window.message.userBubble.bg", e.target.value)}
                      className="form-control-custom"
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label-custom">Bot Bubble Color</label>
                  <div className="d-flex align-items-center gap-2">
                    <div
                      style={{
                        width: "38px",
                        height: "38px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        flexShrink: 0,
                        border: "1px solid #e2e8f0"
                      }}
                    >
                      <input
                        type="color"
                        value={settings.window.message.botBubble.bg}
                        onChange={(e) =>
                          updateSetting(
                            "window.message.botBubble.bg",
                            e.target.value
                          )
                        }
                        style={{
                          width: "150%",
                          height: "150%",
                          padding: 0,
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          transform: "translate(-25%, -25%)"
                        }}
                      />
                    </div>
                    <input
                      type="text"
                      value={settings.window.message.botBubble.bg}
                      onChange={(e) => updateSetting("window.message.botBubble.bg", e.target.value)}
                      className="form-control-custom"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "advanced" && (
              <div className="d-flex flex-column gap-3">
                <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                  <span style={{ width: "4px", height: "18px", background: "var(--brand-primary)", borderRadius: "2px" }}></span>
                  Advanced Sizing
                </h6>
                <div>
                  <label className="form-label-custom">Window Width: {settings.advanced.window.width}px</label>
                  <input
                    type="range"
                    min="300"
                    max="500"
                    value={settings.advanced.window.width}
                    onChange={(e) =>
                      updateSetting(
                        "advanced.window.width",
                        Number(e.target.value)
                      )
                    }
                    style={{ width: "100%", cursor: "pointer" }}
                    className="form-range"
                  />
                </div>

                <div>
                  <label className="form-label-custom">Window Height: {settings.advanced.window.height}px</label>
                  <input
                    type="range"
                    min="400"
                    max="700"
                    value={settings.advanced.window.height}
                    onChange={(e) =>
                      updateSetting(
                        "advanced.window.height",
                        Number(e.target.value)
                      )
                    }
                    style={{ width: "100%", cursor: "pointer" }}
                    className="form-range"
                  />
                </div>

                <button
                  onClick={() => setSettings(DEFAULT_SETTINGS)}
                  style={{
                    marginTop: "24px",
                    padding: "10px 20px",
                    background: "#fef2f2",
                    color: "#ef4444",
                    border: "1px solid #fee2e2",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "14px",
                    transition: "all 0.2s",
                    width: "100%"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#fee2e2";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#fef2f2";
                  }}
                >
                  Reset to Defaults
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Preview Panel */}
        <div className="widget-builder-preview">
          {/* Mobile Frame Container */}
          <div className="preview-device-frame">
            <div className="preview-content-wrapper" style={{
              position: "relative",
              width: `${settings.advanced.window.width}px`,
              height: `${settings.advanced.window.height + 100}px`, // Extra space for launcher
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>

              {/* Simulated Page Background */}
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, opacity: 0.5, pointerEvents: "none", backgroundImage: "radial-gradient(#cbd5e1 1px, transparent 1px)", backgroundSize: "20px 20px" }}></div>

              {/* Launcher Button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                  ...getPosition(),
                  width: `${getLauncherSize()}px`,
                  height: `${getLauncherSize()}px`,
                  borderRadius: getLauncherRadius(),
                  background: settings.launcher.primaryColor,
                  border: "none",
                  color: "white",
                  fontSize: "24px",
                  cursor: "pointer",
                  boxShadow: settings.launcher.shadow
                    ? "0 8px 25px rgba(0,0,0,0.15)"
                    : "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                  animation:
                    settings.launcher.animation === "bounce"
                      ? "bounce 1s infinite"
                      : settings.launcher.animation === "pulse"
                        ? "pulse 2s infinite"
                        : settings.launcher.animation === "spin"
                          ? "spin 1s infinite"
                          : settings.launcher.animation === "spin-reverse"
                            ? "spin reverse 1s infinite"
                            : "none",
                  transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)"
                }}
              >
                {settings.launcher.iconUrl ? (
                  <img
                    src={resolveLogoUrl(settings.launcher.iconUrl)}
                    alt="launcher-icon"
                    style={{
                      width: `${getLauncherSize() - 20}px`,
                      height: `${getLauncherSize() - 20}px`,
                      objectFit: settings.launcher.iconFit || "cover",
                      borderRadius: "8px",
                    }}
                  />
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {isOpen ? (
                      <path d="M18 6L6 18M6 6l12 12"></path>
                    ) : (
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    )}
                  </svg>
                )}
              </button>

              {/* Chat Window */}
              <div
                className="preview-chat-window"
                style={{
                  ...getPosition(),
                  bottom: settings.launcher.position.includes("bottom") ? "90px" : "auto",
                  top: settings.launcher.position.includes("top") ? "90px" : "auto",
                  width: `${settings.advanced.window.width}px`,
                  height: `${settings.advanced.window.height}px`,
                  background: "white",
                  borderRadius: "16px",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  opacity: isOpen ? 1 : 0,
                  transform: isOpen
                    ? "scale(1) translateY(0)"
                    : "scale(0.9) translateY(20px)",
                  pointerEvents: isOpen ? "auto" : "none",
                  transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
                  border: "1px solid rgba(0,0,0,0.05)",
                  zIndex: 10
                }}
              >
                {/* Header */}
                <div
                  style={{
                    background: settings.window.header.background,
                    color: settings.window.header.titleColor,
                    padding: "16px 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                  }}
                >
                  <span
                    style={{
                      fontWeight: "600",
                      fontSize: "15px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      {settings.launcher.iconUrl ? (
                        <img
                          src={resolveLogoUrl(settings.launcher.iconUrl)}
                          alt="logo"
                          style={{
                            width: 20,
                            height: 20,
                            objectFit: settings.launcher.iconFit || "cover",
                            borderRadius: "4px",
                          }}
                        />
                      ) : (
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                      )}
                    </div>
                    {settings.window.header.title}
                  </span>
                  <button
                    onClick={() => setIsOpen(false)}
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      border: "none",
                      color: settings.window.header.titleColor,
                      width: "28px",
                      height: "28px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "18px"
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Messages */}
                <div
                  style={{
                    flex: 1,
                    padding: "20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    overflowY: "auto",
                    background: "#f8fafc"
                  }}
                >
                  <div style={{ textAlign: "center", marginBottom: "10px" }}>
                    <span style={{ fontSize: "11px", color: "#94a3b8", background: "#e2e8f0", padding: "4px 8px", borderRadius: "12px" }}>Today</span>
                  </div>

                  <div
                    style={{
                      alignSelf: "flex-start",
                      background: settings.window.message.botBubble.bg,
                      color: settings.window.message.botBubble.text,
                      padding: "12px 16px",
                      borderRadius: "16px 16px 16px 4px",
                      fontSize: `${settings.window.message.fontSize}px`,
                      maxWidth: "85%",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      lineHeight: "1.5"
                    }}
                  >
                    Hello! 👋 How can I help you today?
                  </div>

                  <div
                    style={{
                      alignSelf: "flex-end",
                      background: settings.window.message.userBubble.bg,
                      color: settings.window.message.userBubble.text,
                      padding: "12px 16px",
                      borderRadius: "16px 16px 4px 16px",
                      fontSize: `${settings.window.message.fontSize}px`,
                      maxWidth: "85%",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      lineHeight: "1.5"
                    }}
                  >
                    I want to customize this widget!
                  </div>

                  <div
                    style={{
                      alignSelf: "flex-start",
                      background: settings.window.message.botBubble.bg,
                      color: settings.window.message.botBubble.text,
                      padding: "12px 16px",
                      borderRadius: "16px 16px 16px 4px",
                      fontSize: `${settings.window.message.fontSize}px`,
                      maxWidth: "85%",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      lineHeight: "1.5"
                    }}
                  >
                    Great choice! You can change colors, icons, and more using the panel on the left. 🎨
                  </div>
                </div>

                {/* Input */}
                <div
                  style={{
                    padding: "16px",
                    background: "white",
                    borderTop: "1px solid #f1f5f9",
                    display: "flex",
                    gap: "10px",
                    alignItems: "center"
                  }}
                >
                  <input
                    placeholder={settings.window.input.placeholder}
                    disabled
                    style={{
                      flex: 1,
                      padding: "12px 16px",
                      border: `1px solid ${settings.window.input.border}`,
                      borderRadius: "24px",
                      outline: "none",
                      fontSize: `${settings.window.message.fontSize}px`,
                      background: settings.window.input.bg,
                      color: settings.window.input.text,
                    }}
                    className="preview-input"
                  />
                  <button
                    style={{
                      background: settings.window.sendButton.bg,
                      color: settings.window.sendButton.text,
                      border: "none",
                      width: settings.window.sendButton.type === "icon" ? "44px" : "auto",
                      height: "44px",
                      padding: settings.window.sendButton.type === "text" ? "0 20px" : "0",
                      borderRadius: "22px",
                      cursor: "pointer",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                      fontSize: "14px"
                    }}
                  >
                    {settings.window.sendButton.type === "icon"
                      ? renderIcon(settings.window.sendButton.icon, 18)
                      : settings.window.sendButton.buttonText
                    }
                  </button>
                </div>

                {/* Branding Footer */}
                <div style={{ textAlign: "center", padding: "8px", background: "#f8fafc", fontSize: "10px", color: "#94a3b8" }}>
                  Powered by <a href="https://meylo.co.uk" target="_blank" rel="noopener noreferrer">Meylo</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-10px); }
            60% { transform: translateY(-5px); }
          }
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
          /* Theme tokens for widget customization builder */
          .widget-customization {
            --wc-body: #f8fafc;
            --wc-panel: #ffffff;
            --wc-border: #e2e8f0;
            --wc-text-primary: #0f172a;
            --wc-text-secondary: #334155;
            --wc-text-muted: #64748b;
            --wc-control-bg: #f8fafc;
            --wc-shadow: 0 20px 60px rgba(0,0,0,0.15);
            padding: 24px;
          }
          [data-bs-theme="dark"] .widget-customization {
            --wc-body: #0f172a;
            --wc-panel: #1e293b;
            --wc-border: #334155;
            --wc-text-primary: #f8fafc;
            --wc-text-secondary: #cbd5e1;
            --wc-text-muted: #94a3b8;
            --wc-control-bg: #111827;
            --wc-shadow: 0 24px 60px rgba(0,0,0,0.45);
          }

          .widget-builder-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 24px;
            height: calc(100vh - 120px);
            min-height: 700px;
            max-height: 900px;
            align-items: start;
            min-width: 0;
          }

          .widget-builder-settings {
            display: flex;
            flex-direction: column;
            gap: 16px;
            height: 100%;
            overflow-y: auto;
            padding-right: 8px;
            min-width: 0;
          }

          .widget-builder-preview {
            position: sticky;
            top: 0;
            height: calc(100vh - 120px);
            min-height: 700px;
            max-height: 900px;
            background: var(--wc-body);
            border-radius: 20px;
            border: 1px solid var(--wc-border);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 40px;
            overflow: hidden;
            box-shadow: inset 0 0 40px rgba(0,0,0,0.03);
          }

          @media (max-width: 991px) {
            .widget-customization {
              padding: 16px;
            }
            .widget-builder-grid {
              grid-template-columns: 1fr;
              height: auto;
              min-height: auto;
              max-height: none;
              gap: 40px;
            }
            .widget-builder-settings {
              height: auto;
              overflow-y: visible;
              padding-right: 0;
            }
            .widget-builder-preview {
              position: static;
              height: 850px;
              min-height: 850px;
              padding: 16px;
              border-radius: 24px;
              background: linear-gradient(180deg, var(--wc-body) 0%, rgba(226, 232, 240, 0.3) 100%);
              border: 1px dashed var(--wc-border);
            }
            .chatbots-double-tabs {
              flex-wrap: nowrap !important;
              overflow-x: auto;
              padding-bottom: 12px;
              margin-bottom: 24px;
              -webkit-overflow-scrolling: touch;
              scrollbar-width: none;
              cursor: grab;
              touch-action: pan-x;
            }
            .chatbots-double-tabs:active {
              cursor: grabbing;
            }
            .chatbots-double-tabs::-webkit-scrollbar {
              display: none;
            }
            
            .form-label-custom {
              font-size: 14px;
              margin-bottom: 10px;
              color: var(--wc-text-primary);
            }
            .form-control-custom {
              height: 48px;
              font-size: 16px; /* Prevent zoom on iOS */
              background-color: var(--wc-panel);
              border-radius: 12px;
            }
            .row.g-3 > [class*="col-"] {
              margin-bottom: 8px;
            }
            
            .d-flex.flex-column.gap-3 {
              gap: 24px !important;
            }
          }

          /* Preview Scale & Frame Logic */
          .preview-device-frame {
            width: 100%;
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            overflow: hidden;
            position: relative;
          }

          .preview-content-wrapper {
            transform-origin: center center;
            transition: transform 0.3s ease;
          }

          @media (max-width: 576px) {
            .preview-content-wrapper {
              transform: scale(0.8);
            }
          }

          @media (max-width: 480px) {
            .preview-content-wrapper {
              transform: scale(0.7);
            }
          }

          @media (max-width: 400px) {
            .preview-content-wrapper {
              transform: scale(0.65);
            }
          }

          @media (max-width: 360px) {
            .preview-content-wrapper {
              transform: scale(0.6);
            }
          }

          /* Preview input placeholder styling */
          .preview-input::placeholder {
            color: inherit !important;
            opacity: 0.6;
          }
          
          /* Scrollbar styling */
          .widget-customization ::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          .widget-customization ::-webkit-scrollbar-track {
            background: transparent;
          }
          .widget-customization ::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 3px;
          }
          .widget-customization ::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `,
        }}
      />
    </div>
  );
};

export default WidgetCustomizationBuilder;
